import dayjs from 'dayjs';

/**
 * Calculates derived market data fields based on the input data
 * @param {Object} data - The market data object containing price and other information
 * @param {Object} [options={}] - Options for calculation
 * @param {boolean} [options.usePrevCloseDayForMcap=false] - If true, use prevClose for market cap calculation, otherwise use last
 * @returns {Object} The input data object with calculated fields added/updated
 */
export const calculateMarketData = (data, options = {}) => {
  const { usePrevCloseDayForMcap = false } = options;
  // Create a copy of the input data to avoid mutating the original
  const result = { ...data };

  // Calculate market status
  result.marketStatus =  typeof(result.marketStatus) === 'boolean' ? result.marketStatus ? 'Open' : 'Close' : result.marketStatus;

  // Calculate market cap - use prevClose if usePrevCloseDayForMcap is true, otherwise use last
  const priceForMarketCap = usePrevCloseDayForMcap ? result.prevClose : result.last;
  result.marketCap = priceForMarketCap * result.noShares;

  // Calculate P/E ratio
  result.pE = result.eps ? result.last / result.eps : null;

  // Calculate percentage changes
  result.percent52W = result.closePrice52W ? ((result.last - result.closePrice52W) / result.closePrice52W) * 100 : null;
  result.yTD = result.closePriceYtd ? ((result.last - result.closePriceYtd) / result.closePriceYtd) * 100 : null;
  result.changePercentage = result.prevClose ? ((result.last - result.prevClose) / result.prevClose) * 100 : null;
  
  // Calculate price changes
  result.change = result.last - result.prevClose;

  // Calculate high/low values
  result.high = Math.max(result.high, result.last);
  result.low = Math.min(result.low, result.last);
  result.high52W = Math.max(result.high52W, result.last);
  result.low52W = Math.min(result.low52W, result.last);
  result.allTimeHigh = Math.max(result.allTimeHigh, result.last);
  result.allTimeLow = Math.min(result.allTimeLow, result.last);

  return result;
}; 

/**
 * Merges realtime market data with existing instrument data
 * @param {Object} item - The existing instrument data
 * @param {Object} realtimeData - The realtime data to merge
 * @returns {Object} The merged instrument data
 */
export const mergeRealtimeMarketData = (item, realtimeData) => {
  if (!realtimeData) return item;

  const fieldsToMerge = [
    { realtime: 'date', target: 'lastUpdatedDate', transform: (val, original) => new Date(val || original).toISOString() },
    { realtime: 'last', target: 'last' },
    { realtime: 'bid', target: 'bid' },
    { realtime: 'bidSize', target: 'bidSize' },
    { realtime: 'ask', target: 'ask' },
    { realtime: 'askSize', target: 'askSize' },
    { realtime: 'high', target: 'high' },
    { realtime: 'low', target: 'low' },
    { realtime: 'open', target: 'open' },
    { realtime: 'volume', target: 'volume' },
    { realtime: 'todayTurnover', target: 'todayTurnover' },
    { realtime: 'vwap', target: 'vwap' }
  ];

  return fieldsToMerge.reduce((merged, { realtime, target, transform }) => {
    if (realtimeData[realtime] !== undefined) {
      merged[target] = transform 
        ? transform(realtimeData[realtime], item[target])
        : realtimeData[realtime] || item[target];
    }
    return merged;
  }, { ...item });
};

/**
 * Merges stock snapshot and trades data, taking the most recent data for each instrument
 * @param {Object.<string, ISnapshotData>} stockSnapshot - Object containing stock snapshot data
 * @param {Object.<string, ITradesData>} stockTrades - Object containing stock trades data
 * @returns {Object.<string, ISnapshotData>} Merged data with the most recent information for each instrument
 */
export const mergeStockData = (stockSnapshot, stockTrades) => {
  const allInstrumentIds = [...new Set([...Object.keys(stockSnapshot), ...Object.keys(stockTrades)])];
  
  return allInstrumentIds.reduce((result, instrumentId) => {
    const snapshot = stockSnapshot[instrumentId];
    const trades = stockTrades[instrumentId];

    if (snapshot && trades) {
      // If both exist, take the one with the most recent date
      if (dayjs(snapshot.date).isAfter(trades.date)) {
        result[instrumentId] = snapshot;
      } else {
        // When trades is more recent, merge trades data into snapshot structure
        result[instrumentId] = { ...snapshot, ...trades };
      }
    } else {
      // If only one exists, use that one
      result[instrumentId] = snapshot || trades;
    }
    
    return result;
  }, {});
}; 